import React, { useState, useEffect, ChangeEvent } from 'react';

import { useStore } from '@vromo/mobx-react';
import { observer } from 'mobx-react-lite';
import { update } from 'serializr';

import { Vehicle } from '@vromo/models';

import Tracking from '@vromo/analytics';

import Title from '@vromo/ui/components/Title';
import { ModalHeader, ModalContent, ModalActions } from '@vromo/ui/components/Modal';
import Button from '@vromo/ui/components/Button';
import Select from '@vromo/ui/components/Select';
import Input from '@vromo/ui/components/Input';
import { useToasts } from '@vromo/ui/components/Toasts';
import { RootStore } from '../../stores/RootStore';
import { qaLabelTeams } from '../../../../../common-qa-labels/teams';

interface Props {
    close: () => void;
}

export default observer<Props>(function NewDriverForm({ close }) {
    const {
        groupStore,
        vehicleStore,
        authStore: { role, graph }
    } = useStore<RootStore>('rootStore');

    const showToast = useToasts();

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [group, setGroup] = useState(role.group!.id);
    const [external, setExternal] = useState<string>();

    useEffect(() => {
        Tracking.track('Opened New Driver Form');
    }, []);

    const isValidDriver = () => {
        const allFieldsFilled = email.length !== 0 && name.length !== 0 && group !== null;

        const validEmail = /@.+/.test(email);

        const driverExistsInGroup = vehicleStore
            .withGroup(group!)
            .some((v: Vehicle) => v.driver?.contact?.email === email);

        const showError = (validated: boolean, message: string) => {
            if (validated) {
                return true;
            } else {
                showToast(message);
                return false;
            }
        };

        return (
            showError(allFieldsFilled, 'Please fill out all fields before adding a new driver.') &&
            showError(validEmail, `The email you've entered is invalid!`) &&
            showError(!driverExistsInGroup, `This driver already exists within this group!`)
        );
    };

    const handleSubmit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        if (isValidDriver()) {
            graph.root
                .graph()
                .roles(role.id)
                .vehicles()
                .post([
                    {
                        name: name,
                        group: group,
                        external,
                        driver: {
                            contact: { email }
                        }
                    }
                ])
                .then(({ items }) => {
                    Tracking.track('Driver Created');

                    update(vehicleStore.vehicles, { items, type: 'create' });

                    close();
                })
                .catch((err) => {
                    console.error(err);
                    showToast(`Something went wrong with creating this driver.`);
                });
        }
    };

    const updateName = (newName: string) => {
        setName(newName);
    };

    const handleEmailChange = (email: string) => {
        setEmail(email);
    };

    return (
        <>
            <ModalHeader>
                <Title  data-testid={qaLabelTeams.newDriverModal.title}>Add New Driver</Title>
            </ModalHeader>
            <ModalContent style={{ overflow: 'unset' }}>
                <fieldset>
                    <Input
                        label="Email Address"
                        type="text"
                        value={email}
                        onChange={(e) => handleEmailChange(e.target.value)}
                        data-testid={qaLabelTeams.newDriverModal.emailInput}
                    />
                </fieldset>
                <fieldset>
                    <Input
                        label="Driving for..."
                        tooltip="This is the name that is shown to drivers when switching between different groups in the driver app."
                        type="text"
                        value={name}
                        onChange={(e) => updateName(e.target.value)}
                        placeholder="e.g. Planet Express"
                        data-testid={qaLabelTeams.newDriverModal.drivingForInput}
                    />
                </fieldset>
                <fieldset>
                    <label>Group</label>
                    <Select 
                        data-testid={qaLabelTeams.newDriverModal.groupDropdown}
                        className="form-control input-sm"
                        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                            setGroup(parseInt(e.target.value))
                        }>
                        {groupStore.groups.map((it) => (
                            <option key={it.id} value={it.id!}>
                                {it.name}
                            </option>
                        ))}
                    </Select>
                </fieldset>
                <fieldset>
                    <Input
                        label="External Identifier"
                        tooltip="This could be an employee number, payroll ID. Useful when linking drivers with your existing systems."
                        optional
                        type="text"
                        value={external}
                        onChange={(e) => setExternal(e.target.value)}
                        placeholder="e.g. 05275325"
                        data-testid={qaLabelTeams.newDriverModal.externalIdentifierInput}
                    />
                </fieldset>
            </ModalContent>
            <ModalActions>
                <Button onClick={close}>Cancel</Button>
                <Button primary onClick={handleSubmit} data-testid={qaLabelTeams.newDriverModal.addDriverButton}>
                    Add driver
                </Button>
            </ModalActions>
        </>
    );
});
